import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught:', error.message, info.componentStack?.slice(0, 300));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '50vh', gap: 16, padding: 32, textAlign: 'center',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--crit, #f44)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg-0, #fff)' }}>Something went wrong</div>
          <div style={{ fontSize: 13, color: 'var(--fg-3, #888)', maxWidth: 400, lineHeight: 1.6 }}>
            An unexpected error occurred. Try refreshing the page. If this persists, contact support.
          </div>
          <pre style={{ fontSize: 11, color: 'var(--fg-4, #666)', fontFamily: 'var(--font-mono, monospace)', maxWidth: 500, overflow: 'auto', padding: 12, background: 'var(--bg-2, #1a1a1a)', borderRadius: 8, textAlign: 'left' }}>
            {this.state.error?.message}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: 'var(--accent, #6366f1)', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
